import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Pencil, Trash2, Copy } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { QuotationResponse, DocumentStatusEnum } from '@/app/types/quotation';
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header';

// Custom events for row actions
export const editQuotationEvent = 'editQuotation';
export const deleteQuotationEvent = 'deleteQuotation';
export const convertQuotationEvent = 'convertQuotationToSO';

// Helper functions
const formatCurrency = (amount: string | number | null | undefined): string => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(amount));
};

const formatDate = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch (error) {
    return String(dateString);
  }
};

// Status Badge Logic
type BadgeVariant = "default" | "secondary" | "destructive" | "outline" | null | undefined;

const getStatusBadgeVariant = (status: string): BadgeVariant => {
  switch (status?.toLowerCase()) {
    case 'draft': return 'secondary';
    case 'confirmed': return 'default';
    case 'expired': return 'outline';
    case 'cancelled': return 'destructive';
    case 'converted':
    case 'completed':
      return 'default'; // Use a safe default value
    default: return 'outline';
  }
};

export const columns: ColumnDef<QuotationResponse>[] = [
  {
    accessorKey: 'document_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Quote #" />,
    cell: ({ row }): JSX.Element => <span className="font-medium">{row.getValue('document_number')}</span>,
  },
  {
    accessorKey: 'transaction_date',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    cell: ({ row }): JSX.Element => <span>{formatDate(row.getValue('transaction_date'))}</span>,
  },
  {
    accessorKey: 'party.name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Customer" />,
    cell: ({ row }): JSX.Element => <span>{row.original.party?.name ?? '-'}</span>,
  },
  {
    accessorKey: 'total_amount',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
    cell: ({ row }): JSX.Element => <span className="font-medium text-right">{formatCurrency(row.original.total_amount)}</span>,
    sortingFn: 'basic',
  },
  {
    accessorKey: 'valid_until_date',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Valid Until" />,
    cell: ({ row }): JSX.Element => <span>{formatDate(row.original.valid_until_date)}</span>,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }): JSX.Element => {
      const status = row.getValue('status') as string;
      return (
        <Badge variant={getStatusBadgeVariant(status)} className="capitalize">
          {status?.replace('_', ' ') ?? 'N/A'}
        </Badge>
      );
    },
    filterFn: (row, id, value): boolean => {
      return value.includes(row.getValue(id));
    },
  },
  {
    id: 'actions',
    cell: ({ row }): JSX.Element => {
      const quotation = row.original;
      const isConvertible = quotation.status === DocumentStatusEnum.CONFIRMED;

      const handleEdit = (): void => {
        document.dispatchEvent(new CustomEvent(editQuotationEvent, { detail: { quotation } }));
      };
      
      const handleDelete = (): void => {
        document.dispatchEvent(new CustomEvent(deleteQuotationEvent, { detail: { quotation } }));
      };
      
      const handleConvert = (): void => {
        document.dispatchEvent(new CustomEvent(convertQuotationEvent, { detail: { quotation } }));
      };

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleEdit}>
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </DropdownMenuItem>
            {isConvertible && (
              <DropdownMenuItem onClick={handleConvert}>
                <Copy className="mr-2 h-4 w-4" /> Convert to Sales Order
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={handleDelete} disabled={quotation.status !== DocumentStatusEnum.DRAFT}>
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
]; 